/**
 * Hook para buscar permissões do usuário por loja (store-scoped)
 * Usa a RPC get_user_store_permissions que deriva permissões das features atribuídas
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { StorePermission } from '@/types/shop';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useSessionContext } from '@/context/SessionContextProvider';

interface UseStorePermissionsProps {
  storeId: string | null;
  enabled?: boolean;
}

interface PermissionRow {
  permission: string;
}

/**
 * Mapeamento de permissões legadas do banco para permissões atuais do frontend.
 * A RPC get_user_store_permissions retorna valores como "view_clients", 
 * mas o frontend agora espera "view_pessoas".
 */
const PERMISSION_LEGACY_MAP: Record<string, string> = {
  // Módulo Pessoas (antigo Clientes)
  'view_clients': 'view_pessoas',
  'create_clients': 'create_pessoas',
  'edit_clients': 'edit_pessoas',
  'delete_clients': 'delete_pessoas',
  // Módulo Gerenciar (antigo Equipe) - se aplicável
  'view_equipe': 'view_gerenciar',
  'manage_equipe': 'manage_gerenciar',
};

/**
 * Normaliza uma permissão do banco para a versão atual do frontend.
 * Se não houver mapeamento, retorna a permissão original.
 */
function normalizeStorePermission(permission: string): StorePermission | null {
  const normalized = PERMISSION_LEGACY_MAP[permission] || permission;
  
  // Verificar se é uma permissão válida do enum StorePermission
  if (Object.values(StorePermission).includes(normalized as StorePermission)) {
    return normalized as StorePermission;
  }
  
  // Se a permissão original era válida, retorná-la
  if (Object.values(StorePermission).includes(permission as StorePermission)) {
    return permission as StorePermission;
  }
  
  // Permissão desconhecida - filtrar
  console.warn('⚠️ useStorePermissions - Permissão desconhecida ignorada:', permission);
  return null;
}

export const useStorePermissions = ({ storeId, enabled = true }: UseStorePermissionsProps) => {
  const { user } = useSupabaseAuth();
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['store-permissions', user?.id, storeId],
    queryFn: async (): Promise<StorePermission[]> => {
      if (!user?.id || !storeId) {
        return [];
      }

      // Masters têm todas as permissões
      if (user.isMaster) {
        return Object.values(StorePermission);
      }

      const { data, error } = await supabase.rpc('get_user_store_permissions', {
        _user_id: user.id,
        _store_id: storeId,
      });

      if (error) {
        console.error('❌ useStorePermissions - Erro ao buscar permissões:', error);
        return [];
      }

      // Mapear resultado para array de StorePermission com normalização
      const rawPermissions = (data || []).map((row: PermissionRow) => row.permission);
      
      // Normalizar e filtrar permissões inválidas
      const normalizedPermissions = rawPermissions
        .map((p: string) => normalizeStorePermission(p))
        .filter((p): p is StorePermission => p !== null);
      
      // Deduplicar (caso haja mapeamentos duplicados)
      const permissions = [...new Set(normalizedPermissions)];
      
      /* console.log('✅ useStorePermissions - Permissões carregadas:', {
        userId: user.id,
        storeId,
        rawCount: rawPermissions.length,
        normalizedCount: permissions.length,
        raw: rawPermissions,
        permissions,
      }); */

      return permissions;
    },
    enabled: enabled && !!user?.id && !!storeId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
    refetchOnWindowFocus: false,
  });

  /**
   * Verifica se o usuário tem uma permissão específica na loja atual
   */
  const hasPermission = (permission: StorePermission): boolean => {
    // Durante loading, retornar false para evitar flash de conteúdo não autorizado
    if (query.isLoading) {
      return false;
    }

    // Master tem tudo
    if (user?.isMaster) {
      return true;
    }

    return query.data?.includes(permission) ?? false;
  };

  /**
   * Verifica se o usuário tem qualquer uma das permissões
   */
  const hasAnyPermission = (permissions: StorePermission[]): boolean => {
    if (query.isLoading) {
      return false;
    }

    if (user?.isMaster) {
      return true;
    }

    return permissions.some(p => query.data?.includes(p));
  };

  /**
   * Verifica se o usuário tem todas as permissões
   */
  const hasAllPermissions = (permissions: StorePermission[]): boolean => {
    if (query.isLoading) {
      return false;
    }

    if (user?.isMaster) {
      return true;
    }

    return permissions.every(p => query.data?.includes(p));
  };

  /**
   * Invalida o cache de permissões (útil ao trocar de loja)
   */
  const invalidatePermissions = () => {
    queryClient.invalidateQueries({ queryKey: ['store-permissions', user?.id] });
  };

  return {
    permissions: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    invalidatePermissions,
    refetch: query.refetch,
  };
};

/**
 * Hook simplificado que usa o storeId do SessionContext
 * Para uso em componentes que já estão dentro do contexto de loja
 */
/**
 * Hook simplificado que usa o storeId do SessionContext
 * Para uso em componentes que já estão dentro do contexto de loja
 */
export const useCurrentStorePermissions = () => {
  const { storeId } = useSessionContext();
  return useStorePermissions({ storeId });
};
